import { DailyData } from '../../model/dto/daily-data';
import { TryingToConceiveStatus } from '../../model/dto/trying-to-conceive-status.enum';
import { HealthGoalType } from '../../model/dto/health-goal-type.enum';
import Colours from '../../../design-system/colours';
import i18n from '../../../i18n/config';

type FertilityStatusCopyVariant = 'ttc' | 'nonTtc';

const mapHealthGoalToCopyVariant = (
  healthGoal?: string
): FertilityStatusCopyVariant => {
  switch (healthGoal?.toLowerCase()) {
    case HealthGoalType.PERIMENOPAUSE_TRACKING.toLowerCase():
    case HealthGoalType.HORMONE_OPTIMIZATION.toLowerCase():
      return 'nonTtc';
    default:
      return 'ttc';
  }
};

const TTC_STATUS_LABEL_KEYS: Partial<Record<TryingToConceiveStatus, string>> = {
  [TryingToConceiveStatus.LOW]: 'calendarInfo.lowFertility',
  [TryingToConceiveStatus.FERTILE]: 'calendarInfo.fertile',
  [TryingToConceiveStatus.HIGH]: 'calendarInfo.highFertility',
  [TryingToConceiveStatus.PEAK]: 'calendarInfo.peak',
  [TryingToConceiveStatus.POST_OVULATION]: 'calendarInfo.postOvulation',
};

const NON_TTC_STATUS_LABEL_KEYS: Partial<
  Record<TryingToConceiveStatus, string>
> = {
  [TryingToConceiveStatus.PERIOD]: 'calendarInfo.nonTtc.period',
  [TryingToConceiveStatus.FERTILE]: 'calendarInfo.nonTtc.fertile',
  [TryingToConceiveStatus.LOW]: 'calendarInfo.nonTtc.lowFertility',
  [TryingToConceiveStatus.HIGH]: 'calendarInfo.nonTtc.highFertility',
  [TryingToConceiveStatus.PEAK]: 'calendarInfo.nonTtc.peak',
  [TryingToConceiveStatus.POST_OVULATION]: 'calendarInfo.nonTtc.postOvulation',
};

const STATUS_LABEL_KEYS_BY_GOAL: Record<
  FertilityStatusCopyVariant,
  Partial<Record<TryingToConceiveStatus, string>>
> = {
  ttc: TTC_STATUS_LABEL_KEYS,
  nonTtc: NON_TTC_STATUS_LABEL_KEYS,
};

export class CalendarDayStatusUtils {
  static calculateDayStatus(
    dailyDataList: DailyData[],
    currentDailyDataIndexInList: number
  ): TryingToConceiveStatus | undefined {
    const currentDailyData: DailyData =
      dailyDataList[currentDailyDataIndexInList];

    if (currentDailyDataIndexInList !== -1) {
      return currentDailyData.trying_to_conceive_status;
    }
  }

  static getTryingToConceiveStatusColor(
    tryingToConceiveStatus: TryingToConceiveStatus | undefined
  ) {
    switch (tryingToConceiveStatus) {
      case TryingToConceiveStatus.LOW:
        return 'transparent';
      case TryingToConceiveStatus.PERIOD:
        return Colours.OV_PINK;
      case TryingToConceiveStatus.FERTILE:
        return Colours.OV_FERTILE;
      case TryingToConceiveStatus.HIGH:
        return Colours.OV_HIGH_TRYING_TO_CONCEIVE;
      case TryingToConceiveStatus.PEAK:
        return Colours.OV_HIGH_TRYING_TO_CONCEIVE;
      case TryingToConceiveStatus.POST_OVULATION:
        return Colours.POST_OVULATION;
      default:
        return 'transparent';
    }
  }

  static getTryingToConceiveStatusLabel(
    tryingToConceiveStatus: TryingToConceiveStatus | undefined,
    healthGoal?: string
  ): string | null {
    if (!tryingToConceiveStatus) {
      return null;
    }

    const goalVariant = mapHealthGoalToCopyVariant(healthGoal);
    const labelKey =
      STATUS_LABEL_KEYS_BY_GOAL[goalVariant][tryingToConceiveStatus];

    return labelKey ? i18n.t(labelKey) : null;
  }
}
